import React from 'react';
import { useSelector } from 'react-redux';
import {
  Route,
  Routes as RouteSwitch,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import { DatePicker } from 'antd';
import moment from 'moment';
import styled from 'styled-components';
import { Muted } from 'styles';

import { DATE_FORMAT } from 'appConstants';
import AiChatBotBody from 'components/AiChatBot/AiChatBotBody';
import { PageHeader } from 'components/page';
import DatePartSelect from 'containers/Analytics/components/DatePartSelect';
import { usePermissions, useTenant } from 'hooks';
import ForbiddenPage from 'pages/public/AccessDenied';
import { StepsLoading } from 'pages/public/Loading';
import NotFoundPage from 'pages/public/NotFoundPage';

import { AnalyticsProvider } from './AnalyticsProvider';
import CustomSpin from './components/CustomSpin';
import DemoAlert from './components/DemoAlert';
import { useAnalyticsContext } from './hooks';
import { Campaigns, Merchants, Overview, Performance, Rewards } from './pages';
import Loyalties from './pages/Loyalties';

const DateRangePicker = ({ showPreviousPeriod = false }) => {
  const { dateRange, onDateRateChange } = useAnalyticsContext();

  const diff = moment(dateRange[1]).diff(dateRange[0], 'day') + 1;
  const prevRange = [
    moment(dateRange[0]).subtract(diff, 'day'),
    moment(dateRange[1]).subtract(diff, 'day')
  ];

  return (
    <div>
      <DatePicker.RangePicker
        value={dateRange}
        onChange={onDateRateChange}
        format={DATE_FORMAT}
        disabledDate={current => current && current > moment().endOf('day')}
        allowClear={false}
      />
      {showPreviousPeriod && (
        <div style={{ textAlign: 'center' }}>
          <Muted>
            Compared to {prevRange[0].format(DATE_FORMAT)} - {prevRange[1].format(DATE_FORMAT)}
          </Muted>
        </div>
      )}
    </div>
  );
};

const AnalyticsHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const { features } = useTenant();
  const { isDemoMode, datePart, onDatePartChange } = useAnalyticsContext();

  let featureTabs = [
    { key: 'rewards', label: 'Rewards' },
    { key: 'merchants', label: 'Merchants' },
    { key: 'loyalties', label: 'Loyalty' },
    { key: 'campaigns', label: 'Campaigns' },
    { key: 'performance', label: 'Performance' },
    { key: 'quicksight', label: 'QuickSight' }
  ].filter(tab => features[tab.key]);

  if (isDemoMode) {
    featureTabs = featureTabs.filter(tab => features[tab.key]);
  }

  const navigationTabs = [{ key: 'overview', label: 'Overview' }, ...featureTabs];
  const currentTabLocation = location.pathname.split('business_intelligence')[1]?.split('/')[1];
  const isOverviewOrCampaign =
    currentTabLocation === 'overview' || currentTabLocation === 'campaigns';

  return (
    <PageHeader
      title={'Analytics'}
      customAction={() => (
        <div style={{ display: 'flex' }}>
          <DateRangePicker showPreviousPeriod={isOverviewOrCampaign} />
          {isOverviewOrCampaign && (
            <DatePartSelect
              onChange={onDatePartChange}
              value={datePart}
              style={{ marginLeft: '0.5rem', width: 'auto' }}
            />
          )}
        </div>
      )}
      tabs={navigationTabs}
      onTabChange={tab => {
        navigate(`/p/business_intelligence/${tab}`);
      }}
      defaultTab={currentTabLocation}
      isSticky
    />
  );
};

const Analytics = () => {
  const open = useSelector((state: any) => state.aiChatBot.open);
  const location = useLocation();

  const { loading: permissionsLoading, canView } = usePermissions('business_intelligence');

  if (permissionsLoading) {
    return <StepsLoading />;
  }

  if (!canView) {
    return <ForbiddenPage />;
  }

  let featureRoutes = [
    {
      key: 'rewards',
      route: <Route key={'rewards'} path="rewards/*" element={<Rewards />} />
    },
    {
      key: 'merchants',
      route: <Route key={'merchants'} path="merchants/*" element={<Merchants />} />
    },
    {
      key: 'loyalties',
      route: <Route key={'loyalties'} path="loyalties/*" element={<Loyalties />} />
    },
    {
      key: 'campaigns',
      route: <Route key={'campaigns'} path="campaigns/*" element={<Campaigns />} />
    },
    {
      key: 'performance',
      route: <Route key={'performance'} path="performance/*" element={<Performance />} />
    }
  ];

  return (
    <AnalyticsProvider>
      <Container>
        <PageWrapper>
          <StickyContainer>
            <DemoAlert />
            <AnalyticsHeader />
            <CustomSpin>
              <div
                className="container"
                style={{ overflow: 'visible', position: 'relative', flex: '1 1 auto' }}
              >
                <RouteSwitch>
                  <Route path="overview/*" element={<Overview />} />
                  {featureRoutes.map(r => r.route)}
                  <Route element={<NotFoundPage />} />
                </RouteSwitch>
              </div>
            </CustomSpin>
          </StickyContainer>
        </PageWrapper>

        <AiChatSideBarWrapper open={open}>
          <AiChatBotBody />
        </AiChatSideBarWrapper>
      </Container>
    </AnalyticsProvider>
  );
};

export default Analytics;

const StickyContainer = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
`;

export const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
`;

const PageWrapper = styled.div`
  width: 0%;
  flex: 1;
`;

export const AiChatSideBarWrapper = styled.div<{
  open?: boolean;
}>`
  height: 100vh;
  background: linear-gradient(180deg, #d6eff9 3.69%, #ede9fe 100%);
  flex: 0 0 auto;
  position: sticky;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  z-index: 100;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  width: ${({ open }) => (open ? '448px' : '0')};
  transform: ${({ open }) => (open ? 'translateX(0)' : 'translateX(100%)')};
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;
